import React, { useEffect, useState } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';

const inputStyle = {
  padding: '6px',
  borderRadius: 4,
  border: '1px solid #ccc',
  boxSizing: 'border-box',
};

const buttonStyle = {
  padding: '6px 12px',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const StorageLocationForm = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [editedSortOrder, setEditedSortOrder] = useState('');
  const [editedIsActive, setEditedIsActive] = useState(true);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/storage-locations');
      setLocations(response.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Lagerorte:', error);
      alert('Fehler beim Laden der Lagerorte');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleAdd = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      alert('Bitte einen Lagerort eingeben.');
      return;
    }

    try {
      await api.post('/storage-locations', {
        name: trimmed,
        sortOrder: sortOrder === '' ? 0 : parseInt(sortOrder, 10),
        isActive: true,
      });
      setName('');
      setSortOrder('');
      fetchLocations();
    } catch (error) {
      console.error('Fehler beim Hinzufuegen des Lagerorts:', error);
      alert('Fehler beim Hinzufuegen des Lagerorts');
    }
  };

  const handleEdit = (location) => {
    setEditingId(location.id);
    setEditedName(location.name || '');
    setEditedSortOrder(location.sortOrder ?? '');
    setEditedIsActive(location.isActive !== false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditedName('');
    setEditedSortOrder('');
    setEditedIsActive(true);
  };

  const handleUpdate = async (id) => {
    const trimmed = editedName.trim();
    if (!trimmed) {
      alert('Bitte einen Lagerort eingeben.');
      return;
    }

    try {
      await api.put(`/storage-locations/${id}`, {
        name: trimmed,
        sortOrder: editedSortOrder === '' ? 0 : parseInt(editedSortOrder, 10),
        isActive: editedIsActive,
      });
      handleCancel();
      fetchLocations();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Lagerorts:', error);
      alert('Fehler beim Aktualisieren des Lagerorts');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Lagerort wirklich loeschen?')) return;

    try {
      const response = await api.delete(`/storage-locations/${id}`);
      if (response.status === 200 && response.data?.message) {
        alert(response.data.message);
      }
      fetchLocations();
    } catch (error) {
      console.error('Fehler beim Loeschen des Lagerorts:', error);
      alert('Fehler beim Loeschen des Lagerorts');
    }
  };

  if (loading) {
    return <div>Daten werden geladen...</div>;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: 760 }}>
        <Helmet>
          <title>Lagerorte verwalten</title>
          <meta name="description" content="Lagerorte verwalten" />
        </Helmet>

        <h2 style={{ textAlign: 'center', marginBottom: 20 }}>Lagerorte verwalten</h2>

        <div style={{
          display: 'flex',
          gap: 10,
          marginBottom: 20,
          alignItems: 'stretch',
          flexWrap: 'nowrap',
        }}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Lagerort"
            style={{ ...inputStyle, flex: '1 1 auto', minWidth: 0 }}
          />
          <input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            placeholder="Sort"
            style={{ ...inputStyle, flex: '0 0 120px' }}
          />
          <button
            type="button"
            onClick={handleAdd}
            style={{ ...buttonStyle, flex: '0 0 auto', background: '#28a745', color: '#fff' }}
          >
            Hinzufuegen
          </button>
        </div>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {locations.map(location => (
            <li
              key={location.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 120px 100px 190px',
                gap: 10,
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: '1px solid #ddd',
              }}
            >
              {editingId === location.id ? (
                <>
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    value={editedSortOrder}
                    onChange={(e) => setEditedSortOrder(e.target.value)}
                    style={inputStyle}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={editedIsActive}
                      onChange={(e) => setEditedIsActive(e.target.checked)}
                    />
                    Aktiv
                  </label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" onClick={() => handleUpdate(location.id)} style={{ ...buttonStyle, background: '#007bff', color: '#fff' }}>
                      Speichern
                    </button>
                    <button type="button" onClick={handleCancel} style={{ ...buttonStyle, background: '#6c757d', color: '#fff' }}>
                      Abbrechen
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span>{location.name}</span>
                  <span>{location.sortOrder}</span>
                  <span>{location.isActive === false ? 'Inaktiv' : 'Aktiv'}</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" onClick={() => handleEdit(location)} style={{ ...buttonStyle, background: '#007bff', color: '#fff' }}>
                      Bearbeiten
                    </button>
                    <button type="button" onClick={() => handleDelete(location.id)} style={{ ...buttonStyle, background: '#dc3545', color: '#fff' }}>
                      Loeschen
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default StorageLocationForm;
